import { useEffect, useState } from "react";
import { useT } from "../i18n";
async function call(path: string, body?: any) {
  const r = await fetch("/api/cde" + path, {
    credentials: "include",
    ...(body
      ? {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      : {}),
  });
  const result = await r.json();
  if (!r.ok) throw new Error(result.error);
  return result;
}
export function CdeMilestoneLink({
  projectId,
  milestoneId,
  canEdit,
}: {
  projectId: number;
  milestoneId: string;
  canEdit: boolean;
}) {
  const { t } = useT();
  const [link, setLink] = useState<any>(null),
    [error, setError] = useState(""),
    [stale, setStale] = useState(false),
    [busy, setBusy] = useState(false),
    [kind, setKind] = useState("new"),
    [registry, setRegistry] = useState<any[]>([]),
    [code, setCode] = useState("");
  const load = async () => {
    const data = await call(`/project/${projectId}`);
    setLink(
      data.links.find((l: any) => l.milestone_id === milestoneId) || null,
    );
    setStale(data.stale);
  };
  useEffect(() => {
    load().catch((e) => setError(e.message));
    const timer = setInterval(() => load().catch(() => setStale(true)), 60000);
    return () => clearInterval(timer);
  }, [projectId, milestoneId]);
  useEffect(() => {
    if (kind === "rework")
      call("/registry")
        .then(setRegistry)
        .catch((e) => setError(e.message));
  }, [kind]);
  const send = async () => {
    setBusy(true);
    setError("");
    try {
      setLink(
        await call(
          `/project/${projectId}/milestone/${encodeURIComponent(milestoneId)}`,
          { kind, registry_code: code },
        ),
      );
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const status = link?.last_status;
  return (
    <div className="card mt-12">
      <div className="font-medium">{t("cde.title")}</div>
      {status && (
        <>
          <p className="text-sm">
            {status.process_code} ·{" "}
            {t(`cde.status.${status.work_status || "inbox"}`)}
            {status.workflow_mode === "full" && ` · ${status.label_fr || ""}`}
          </p>
          {status.consultant_name && (
            <p className="text-xs">
              {t("cde.consultant")} : {status.consultant_name}
            </p>
          )}
          {status.next_action && (
            <p className="text-sm">{status.next_action}</p>
          )}
          {(status.delivered_version || status.target_version) && (
            <p className="text-xs">
              {t(status.delivered_version ? "cde.delivered" : "cde.target")} :{" "}
              {status.delivered_version || status.target_version}
            </p>
          )}
          {status.url && (
            <a
              className="btn btn-sm"
              href={status.url}
              target="_blank"
              rel="noreferrer"
            >
              {t("cde.open")}
            </a>
          )}
        </>
      )}
      {link?.last_received_at && (
        <p className="text-xs text-ink-3">
          {t("cde.updated")} :{" "}
          {new Date(link.last_received_at).toLocaleString()}
        </p>
      )}
      {(stale || link?.last_error) && (
        <p className="text-xs text-warn">
          {link?.last_error || t("cde.stale")}
        </p>
      )}
      {error && (
        <p className="text-xs text-err" role="alert">
          {error}
        </p>
      )}
      {canEdit && !link?.delivered_at && (
        <>
          <p className="text-xs text-ink-3">{t("cde.saveFirst")}</p>
          <select
            className="form-select"
            aria-label={t("cde.kind")}
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="new">{t("cde.new")}</option>
            <option value="rework">{t("cde.rework")}</option>
          </select>
          {kind === "rework" && (
            <select
              className="form-select mt-8"
              aria-label={t("cde.process")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            >
              <option value="">{t("cde.choose")}</option>
              {registry.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.code} · {r.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            className="btn btn-accent btn-sm mt-12"
            disabled={busy || (kind === "rework" && !code)}
            onClick={send}
          >
            {t(link ? "cde.retry" : "cde.send")}
          </button>
        </>
      )}
    </div>
  );
}
